import { Injectable } from '@angular/core';
import { arrayRemove, arrayUnion, runTransaction, where, Firestore } from '@angular/fire/firestore';
import { Club } from '@models/clubs';
import { COLLECTIONS } from '@models/collections';
import { Season } from '@models/competitions';
import { Scope, WeekDay } from '@models/masters';
import { Team } from '@models/teams';
import { Observable, combineLatest, firstValueFrom, map, shareReplay, switchMap } from 'rxjs';
import { ClubsService } from './club.service';
import { FirestoreService } from './firestore.service';
import { SeasonManagerService } from './season-manager.service';
import { SeasonsService } from './seasons.service';

@Injectable({
  providedIn: 'root',
})
export class TeamsService {
  private COLLECTION = COLLECTIONS.TEAMS;

  private teams$ = this.seasonManagerService.season$.pipe(
    switchMap((seasonYear) =>
      this.firestore.listDocuments<Team>(this.COLLECTION, where('seasonYear', '==', seasonYear)).pipe(shareReplay(1)),
    ),
  );

  public all$: Observable<(Team & { club?: Club; season?: Season })[]> = this.seasonManagerService.season$.pipe(
    switchMap((seasonYear) =>
      combineLatest([
        this.teams$.pipe(map((teams) => teams.filter((team) => team.seasonYear === seasonYear))),
        this.clubsService.all$,
        this.seasonsService.all$,
      ]),
    ),
    map(([teams, clubs, seasons]) =>
      teams.map((team) => ({
        ...team,
        club: team.clubId ? clubs.find((club) => club.id === team.clubId) : undefined,
        season: seasons.find((s) => s.id === team.seasonId),
      })),
    ),
    shareReplay(1),
  );

  public clubsWithTeams$: Observable<(Club & { teams: Team[] })[]> = combineLatest([
    this.clubsService.all$,
    this.teams$,
  ]).pipe(
    map(([clubs, teams]) => {
      return clubs.map((club) => {
        return {
          ...club,
          teams: teams.filter((team) => team.clubId === club.id),
        };
      });
    }),
    shareReplay(1),
  );

  constructor(
    private firestore: FirestoreService,
    private firestoreDb: Firestore,
    private clubsService: ClubsService,
    private seasonsService: SeasonsService,
    private seasonManagerService: SeasonManagerService,
  ) {}

  get(id: string) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.all$.pipe(map((teams) => teams.find((club) => club.id === id)!));
  }

  getByClubAndScope(clubId: string, scope: string) {
    return this.all$.pipe(map((teams) => teams.filter((team) => team.clubId === clubId && scope === team.scope)));
  }

  getByScope(scope: Scope) {
    return this.all$.pipe(map((teams) => teams.filter((team) => scope === team.scope)));
  }

  getByClub(clubId: string) {
    return this.all$.pipe(map((teams) => teams.filter((team) => team.clubId === clubId)));
  }

  create(user: Omit<Team, 'id'>) {
    return this.firestore.create(this.COLLECTION, user);
  }

  update(id: string, user: Partial<Team>) {
    return this.firestore.update(this.COLLECTION, {
      id,
      ...user,
    });
  }

  async createOrUpdate(id: string | undefined, team: Omit<Team, 'id'>) {
    const season = await firstValueFrom(this.seasonsService.get(team.seasonId));
    team.scope = season.scope;
    team.seasonYear = this.seasonManagerService.getSeason();
    return id ? this.update(id, team) : this.create(team);
  }

  getReport() {
    return this.firestore.listDocuments<Team>(this.COLLECTION);
  }

  deleteForever(teamId: string) {
    return this.firestore.deleteForever(this.COLLECTION, teamId);
  }

  deleteDelegate(teamId: string, userId: string) {
    return this.firestore.update(this.COLLECTION, { id: teamId, delegatesIds: arrayRemove(userId) });
  }
  addDelegate(teamId: string, userId: string) {
    return this.firestore.update(this.COLLECTION, { id: teamId, delegatesIds: arrayUnion(userId) });
  }

  deleteTimeSlotTraining(teamId: string, time: { day: WeekDay; time: string }) {
    return this.firestore.update(this.COLLECTION, { id: teamId, timeSlotsTraining: arrayRemove(time) });
  }

  async changeTeamPlayer(userId: string, fromTeamId: string, toTeamId: string) {
    // Usar transacción para asegurar que ambas operaciones se ejecuten de forma atómica
    return runTransaction(this.firestoreDb, async (transaction) => {
      // Obtener referencias a ambos documentos
      const fromTeamRef = this.firestore.getDocumentRef(this.COLLECTION, fromTeamId);
      const toTeamRef = this.firestore.getDocumentRef(this.COLLECTION, toTeamId);
      
      // Leer los documentos actuales para verificar que existen
      const fromTeamDoc = await transaction.get(fromTeamRef);
      const toTeamDoc = await transaction.get(toTeamRef);
      
      if (!fromTeamDoc.exists() || !toTeamDoc.exists()) {
        throw new Error('Uno o ambos equipos no existen');
      }
      
      // Verificar que el jugador está en el equipo origen
      const fromTeamData = fromTeamDoc.data() as Team;
      if (!fromTeamData.playersIds?.includes(userId)) {
        throw new Error('El jugador no está en el equipo origen');
      }
      
      // Actualizar ambos equipos en la misma transacción
      transaction.update(fromTeamRef, { playersIds: arrayRemove(userId) });
      transaction.update(toTeamRef, { playersIds: arrayUnion(userId) });
    });
  }

  /**
   * Cambia la competición de un equipo dejando coherentes las dos caras de la
   * relación en una única transacción:
   *   · team.seasonId / team.scope
   *   · season(origen).teamIds   -> arrayRemove
   *   · season(destino).teamIds  -> arrayUnion (solo si el equipo está validado,
   *     que es la misma regla que aplica `validateTeam` en el listado)
   *
   * No toca `seasonYear` ni los movimientos económicos: la tarifa se cobra por
   * ámbito y aquí el ámbito no puede cambiar (se valida más abajo).
   */
  async changeSeason(teamId: string, toSeasonId: string) {
    return runTransaction(this.firestoreDb, async (transaction) => {
      const teamRef = this.firestore.getDocumentRef(this.COLLECTION, teamId);
      const teamDoc = await transaction.get(teamRef);
      if (!teamDoc.exists()) {
        throw new Error('El equipo no existe');
      }
      const team = teamDoc.data() as Team;
      if (team.seasonId === toSeasonId) {
        throw new Error('El equipo ya está en esa competición');
      }

      const fromSeasonRef = this.firestore.getDocumentRef(COLLECTIONS.SEASON, team.seasonId);
      const toSeasonRef = this.firestore.getDocumentRef(COLLECTIONS.SEASON, toSeasonId);
      const fromSeasonDoc = await transaction.get(fromSeasonRef);
      const toSeasonDoc = await transaction.get(toSeasonRef);

      if (!toSeasonDoc.exists()) {
        throw new Error('La competición de destino no existe');
      }
      const toSeason = toSeasonDoc.data() as Season;
      if (toSeason.seasonYear !== team.seasonYear) {
        throw new Error(
          `El equipo es de la temporada ${team.seasonYear} y la competición de destino de ${toSeason.seasonYear}`,
        );
      }
      if (toSeason.scope !== team.scope) {
        throw new Error(
          `No se puede cambiar de ámbito (${team.scope} -> ${toSeason.scope}): la inscripción se cobra por ámbito`,
        );
      }

      transaction.update(teamRef, { seasonId: toSeasonId, scope: toSeason.scope });
      if (fromSeasonDoc.exists()) {
        transaction.update(fromSeasonRef, { teamIds: arrayRemove(teamId) });
      }
      transaction.update(toSeasonRef, {
        teamIds: team.validated ? arrayUnion(teamId) : arrayRemove(teamId),
      });
    });
  }

  findOne(teamId: string) {
    return firstValueFrom(this.firestore.getDocument<Team>(this.COLLECTION, teamId));
  }
}
